import { Button, Stack } from "@mui/material";
import styles from "./SpecificationPanel.module.scss";
import React, { memo, useCallback, useMemo } from "react";
import { colorsNames, useColors } from "../../../../hooks/useColors";
import { useShelvingStore, useStoreRoom, useStoreSystem } from "../../../../store";
import shallow from "zustand/shallow";
import CircleColor from "../ColorsPanel/CircleColor/CircleColor";
import { Configurator } from "../../../../controllers/configurator";
import { useLoading } from "../../../../hooks/useLoading";
import { REGEXP_POLKA_STOL } from "../../../../constants";
import { BracketItem, PartInterface } from "../../../../shared";
import { SectionNomenklature } from "../../../../store/types/store";
import useSectionsBySide from "./hooks/useSectionsBySide";
import { usePrice } from "../../../../hooks";
import { getItemsCost } from "../../../../utils/calculation";
import Pdf from "../../../icons/Pdf";
import { generatePDF } from "../../../PDFGenerator";
import { getPDFPages, SectionListElement } from "../../../PDFGenerator/getPDFPages";
import { getImageScene } from "../../../PDFGenerator/canvas/getImageScene";
import { SaveButton } from "../../../SaveButton/SaveButton";
import { ToggleButtons } from "../../../shared/ui/ToggleButtons";
import { SpecificationBySections } from "./SpecificationBySections";
import { SpecificationGeneral } from "./SpecificationGeneral";
import { getSpecificationGeneral } from "./utils";
import { useSaveProject } from "../../../../hooks/useSaveProject";
import { GenerateBluePrintPdf } from "./GenerateBluePrintPdf";
import { isNewSite } from "../../../../utils";

interface SpecificationPanelProps {
    windowDebug: boolean;
    proMode: boolean;
}

export const SpecificationPanel = memo(({ windowDebug, proMode }: SpecificationPanelProps) => {
    const { totalPrice } = usePrice();
    const { colorsItems } = useColors();
    const { parts, setBooleanSystemProperties, generalListSpecification } = useStoreSystem(
        (state) => ({
            parts: state.parts,
            setBooleanSystemProperties: state.setBooleanSystemProperties,
            generalListSpecification: state.generalListSpecification,
        }),
        shallow
    );

    const { roomLength, roomDepth, roomHeight, tireRacks } = useStoreRoom(
        (state) => ({
            roomLength: state.length,
            roomDepth: state.depth,
            roomHeight: state.height,
            tireRacks: state.tireRacks,
        }),
        shallow
    );

    const { setIsLoading, setIsPdfPreparation } = useLoading();

    const {
        getCurrentShelving,
        shelvings,
        sectionsNomenklatureBack,
        sectionsNomenklatureFront,
        sectionsNomenklatureLeft,
        sectionsNomenklatureRight,
    } = useShelvingStore(
        (state) => ({
            getCurrentShelving: state.getCurrentShelving,
            shelvings: state.shelving,
            sectionsNomenklatureBack: state.shelving.back.sectionsNomenklature,
            sectionsNomenklatureFront: state.shelving.front.sectionsNomenklature,
            sectionsNomenklatureLeft: state.shelving.left.sectionsNomenklature,
            sectionsNomenklatureRight: state.shelving.right.sectionsNomenklature,
        }),
        shallow
    );

    const sectionsNomenklature = [
        ...sectionsNomenklatureBack,
        ...sectionsNomenklatureFront,
        ...sectionsNomenklatureLeft,
        ...sectionsNomenklatureRight,
    ];

    const currentShelving = getCurrentShelving();
    const totalHeight = currentShelving.height;
    const totalLength = currentShelving.length;

    const sectionsList = useSectionsBySide();

    const circleStyle = useMemo(() => {
        return {
            width: "20px",
            height: "20px",
            marginRight: 0,
            flex: "1 0 auto",
        };
    }, []);

    const finalTotalPrice = useMemo(() => {
        return totalPrice + getItemsCost();
    }, [totalPrice, tireRacks]);

    const saveProject = useSaveProject();

    const addToBasketHandler = useCallback(async () => {
        const hash = await saveProject({ isShowModal: false, isNewProject: false });

        try {
            setIsLoading(true);
            const basketItems: Record<string, number | string> = {};

            const updateBracketInBasket = (bracket: BracketItem) => {
                if (bracket.isVisible) {
                    bracket.elements.forEach((element) => {
                        if (basketItems[`tovar[${element.bitrixId}]`] === undefined) {
                            basketItems[`tovar[${element.bitrixId}]`] = 1;
                            basketItems[`color[${element.bitrixId}]`] = colorsNames[colorsItems.brackets];
                        } else {
                            if (typeof basketItems[`tovar[${element.bitrixId}]`] === "number") {
                                // @ts-ignore
                                basketItems[`tovar[${element.bitrixId}]`] += 1;
                            }
                        }
                    });
                }
            };

            const updateEdgesInBasket = (parts: PartInterface[]) => {
                parts
                    .filter((p) => p.type?.includes("Ребро жесткости"))
                    .forEach((edge) => {
                        if (basketItems[`tovar[${edge.bitrixId}]`] === undefined) {
                            basketItems[`tovar[${edge.bitrixId}]`] = 1;
                            basketItems[`color[${edge.bitrixId}]`] = colorsNames[colorsItems.brackets];
                        } else {
                            if (typeof basketItems[`tovar[${edge.bitrixId}]`] === "number") {
                                // @ts-ignore
                                basketItems[`tovar[${edge.bitrixId}]`] += 1;
                            }
                        }
                    });
            };

            const updateRodsInBasket = (parts: PartInterface[]) => {
                parts
                    .filter((p) => p.type?.includes("Штанга"))
                    .forEach((rod) => {
                        if (basketItems[`tovar[${rod.bitrixId}]`] === undefined) {
                            basketItems[`tovar[${rod.bitrixId}]`] = 1;
                            basketItems[`color[${rod.bitrixId}]`] = colorsNames[colorsItems.brackets];
                        } else {
                            if (typeof basketItems[`tovar[${rod.bitrixId}]`] === "number") {
                                // @ts-ignore
                                basketItems[`tovar[${rod.bitrixId}]`] += 1;
                            }
                        }
                    });
            };

            sectionsList.forEach((section) =>
                section.items.forEach((item) => {
                    //Подсчет кронштейнов
                    if (item.brackets.isExist) {
                        updateBracketInBasket(item.brackets.left);
                        updateBracketInBasket(item.brackets.right);
                    }

                    if (item.edges) {
                        updateEdgesInBasket(item.parts);
                    }

                    if (item.rods) {
                        updateRodsInBasket(item.parts);
                    }

                    if (basketItems[`tovar[${item.bitrixId}]`] === undefined) {
                        basketItems[`tovar[${item.bitrixId}]`] = 1;
                        if (item.name.match(REGEXP_POLKA_STOL)) {
                            basketItems[`color[${item.bitrixId}]`] = colorsNames[colorsItems.shelves];
                        } else {
                            basketItems[`color[${item.bitrixId}]`] = colorsNames[colorsItems.perfpanels];
                        }
                    } else {
                        if (typeof basketItems[`tovar[${item.bitrixId}]`] === "number") {
                            // @ts-ignore
                            basketItems[`tovar[${item.bitrixId}]`] += 1;
                        }
                    }
                    if (hash) basketItems[`project[${item.bitrixId}]`] = String(window.location.href);
                })
            );

            const basketItemsFormData = new FormData();
            const addBasketItem = (item: { bitrixId: string; value: number }, isConnector = false) => {
                if (hash) basketItems[`project[${item.bitrixId}]`] = String(window.location.href);
                if (basketItems[`tovar[${item.bitrixId}]`] === undefined) {
                    basketItems[`tovar[${item.bitrixId}]`] = item.value;
                    if (isConnector) {
                        basketItems[`color[${item.bitrixId}]`] = colorsNames[colorsItems.brackets];
                    } else {
                        basketItems[`color[${item.bitrixId}]`] = colorsNames[colorsItems.guides];
                    }
                } else {
                    if (typeof basketItems[`tovar[${item.bitrixId}]`] === "number") {
                        // @ts-ignore
                        basketItems[`tovar[${item.bitrixId}]`] += item.value;
                    }
                }
            };
            sectionsNomenklature.forEach((sectionNomenkl) => {
                const verticalGuide480 = {
                    value: sectionNomenkl.vertical["480"],
                    bitrixId: sectionNomenkl.vertical["bitrixId480"] || "0",
                };
                const verticalGuide600 = {
                    value: sectionNomenkl.vertical["600"],
                    bitrixId: sectionNomenkl.vertical["bitrixId600"] || "0",
                };
                const verticalGuide800 = {
                    value: sectionNomenkl.vertical["800"],
                    bitrixId: sectionNomenkl.vertical["bitrixId800"] || "0",
                };
                const verticalGuide1200 = {
                    value: sectionNomenkl.vertical["1200"],
                    bitrixId: sectionNomenkl.vertical["bitrixId1200"] || "0",
                };
                const horizontalGuide512 = {
                    value: sectionNomenkl.horizontal["480"],
                    bitrixId: sectionNomenkl.horizontal["bitrixId480"] || "0",
                };
                const horizontalGuide632 = {
                    value: sectionNomenkl.horizontal["600"],
                    bitrixId: sectionNomenkl.horizontal["bitrixId600"] || "0",
                };
                const horizontalGuide832 = {
                    value: sectionNomenkl.horizontal["800"],
                    bitrixId: sectionNomenkl.horizontal["bitrixId800"] || "0",
                };
                const horizontalGuide1232 = {
                    value: sectionNomenkl.horizontal["1200"],
                    bitrixId: sectionNomenkl.horizontal["bitrixId1200"] || "0",
                };
                const connector = {
                    value: sectionNomenkl.connector,
                    bitrixId: sectionNomenkl.bitrixIdConnector || "0",
                };

                horizontalGuide512.value && addBasketItem(horizontalGuide512);
                horizontalGuide632.value && addBasketItem(horizontalGuide632);
                horizontalGuide832.value && addBasketItem(horizontalGuide832);
                horizontalGuide1232.value && addBasketItem(horizontalGuide1232);
                verticalGuide480.value && addBasketItem(verticalGuide480);
                verticalGuide600.value && addBasketItem(verticalGuide600);
                verticalGuide800.value && addBasketItem(verticalGuide800);
                verticalGuide1200.value && addBasketItem(verticalGuide1200);
                connector.value && addBasketItem(connector, true);
            });
            const { tireRacks } = useStoreRoom.getState();

            tireRacks.forEach((tireRack) => {
                if (basketItems[`tovar[${tireRack.bitrixId}]`] === undefined) {
                    basketItems[`tovar[${tireRack.bitrixId}]`] = 1;
                    basketItems[`color[${tireRack.bitrixId}]`] = colorsNames[colorsItems.tireRacks];
                } else {
                    if (typeof basketItems[`tovar[${tireRack.bitrixId}]`] === "number") {
                        basketItems[`tovar[${tireRack.bitrixId}]`] = +basketItems[`tovar[${tireRack.bitrixId}]`] + 1;
                    }
                }
                if (hash) basketItems[`project[${tireRack.bitrixId}]`] = String(window.location.href);

                if (tireRack.parts) {
                    tireRack.parts.forEach((part) => {
                        if (basketItems[`tovar[${part.id}]`] === undefined) {
                            basketItems[`tovar[${part.id}]`] = 1;
                            basketItems[`color[${part.id}]`] = colorsNames[colorsItems.tireRacks];
                        } else {
                            if (typeof basketItems[`tovar[${part.id}]`] === "number") {
                                basketItems[`tovar[${part.id}]`] = +basketItems[`tovar[${part.id}]`] + 1;
                            }
                        }
                        if (hash) basketItems[`project[${part.id}]`] = String(window.location.href);
                    });
                }
            });
            console.log("Структура корзины:", basketItems);
            const isNew = isNewSite();
            Object.entries(basketItems)
                .filter(([key]) => (isNew ? !key.startsWith("color[") : true))
                .forEach(([key, value]) =>
                    basketItemsFormData.append(key, typeof value === "number" ? `${value}` : value)
                );

            const { data } = await Configurator.addToBasket(basketItemsFormData);
            setIsLoading(false);
            if (data) {
                if (process.env.NODE_ENV !== "development") {
                    window.location.replace(`${window.location.origin}/basket/`);
                }
                return;
            }
        } catch (e) {
            console.log(e);
            setIsLoading(false);
        }
    }, [sectionsList]);

    function generatePdfHandler() {
        if (!parts) {
            return;
        }
        setIsPdfPreparation(true);
        setIsLoading(true);
        setTimeout(() => {
            const allShelving = { ...shelvings };
            Object.values(shelvings).forEach((shelving) => {
                if (shelving.sections.length > 0) {
                    const frontSceneImage = getImageScene(shelving, shelving.name, "front");
                    const sideSceneImage = getImageScene(shelving, shelving.name, "side");
                    allShelving[shelving.name] = { ...allShelving[shelving.name], frontSceneImage };
                    allShelving[shelving.name] = { ...allShelving[shelving.name], sideSceneImage };
                }
            });
            const sectionsListWithNomenclature: SectionListElement[] = sectionsList
                .filter((section) => !section.isIndent)
                .map((section) => {
                    const currentSectionNomenclature = sectionsNomenklature.find(
                        (nomenklature) => nomenklature.sectionId === section.id
                    ) as SectionNomenklature;
                    return { ...section, nomenklature: currentSectionNomenclature };
                });

            const { tireRacks } = useStoreRoom.getState();

            const generalSpecificationForPdf = generalListSpecification
                ? getSpecificationGeneral(sectionsList, sectionsNomenklature, tireRacks)
                : undefined;

            generatePDF(
                getPDFPages(allShelving, sectionsListWithNomenclature, parts, colorsItems, generalSpecificationForPdf)
            );
            setTimeout(() => {
                setIsPdfPreparation(false);
                setIsLoading(false);
            }, 1000);
        }, 500);
    }

    const arrayGeneralListSpecification = ["Общая", "По секциям"];
    if (sectionsNomenklature.length === 0 || sectionsList.length === 0) {
        arrayGeneralListSpecification.length = 1;
    }
    const setGeneralListSpecification = (index: number) => {
        setBooleanSystemProperties([{ name: "generalListSpecification", value: index === 0 }]);
    };

    return (
        <Stack className={styles.blockContainer}>
            <div className={styles.container}>
                <div className={styles.header}>
                    <div className={styles.info}>
                        <div className={styles.name}>Габариты системы</div>
                        <div className={styles.sizes}>{`${totalHeight}х${totalLength}`}мм</div>
                        <div className={styles.price}>
                            {finalTotalPrice.toLocaleString("ru-RU", {
                                style: "currency",
                                currency: "RUB",
                                minimumFractionDigits: 0,
                            })}
                        </div>
                    </div>
                    <div className={styles.colorItems}>
                        <div className={styles.names}>
                            {/*<div className={styles.colorRow}>*/}
                            {/*    <div className={styles.name}>Направляющие (вертикали и горизонтали)</div>*/}
                            {/*    <CircleColor style={circleStyle} isActive={true} color={colorsItems.guides}/>*/}
                            {/*</div>*/}
                            <div className={styles.colorRow}>
                                <div className={styles.name}>Кронштейны</div>
                                <CircleColor style={circleStyle} isActive={true} color={colorsItems.brackets} />
                            </div>
                            {/*<div className={styles.colorRow}>*/}
                            {/*    <div className={styles.name}>Полки</div>*/}
                            {/*    <CircleColor style={circleStyle} isActive={true} color={colorsItems.shelves}/>*/}
                            {/*</div>*/}
                            <div className={styles.colorRow}>
                                <div className={styles.name}>Перфпанели и планки</div>
                                <CircleColor style={circleStyle} isActive={true} color={colorsItems.perfpanels} />
                            </div>
                            {tireRacks.length > 0 && (
                                <div className={styles.colorRow}>
                                    <div className={styles.name}>Стеллажи для колес</div>
                                    <CircleColor style={circleStyle} isActive={true} color={colorsItems.tireRacks} />
                                </div>
                            )}
                        </div>
                    </div>
                    <div>
                        <div className={styles.buttons}>
                            <div onClick={generatePdfHandler} className={styles.pdf}>
                                <Pdf />
                            </div>
                            <GenerateBluePrintPdf />
                            <SaveButton />
                            <Button
                                variant="contained"
                                onClick={addToBasketHandler}
                                sx={{ textTransform: "none", px: 5, py: 1.5, borderRadius: "10px", marginLeft: "10px" }}
                            >
                                Добавить в корзину
                            </Button>
                        </div>
                        <div className={styles.betaMessage}>
                            Данная версия конфигуратора Бета!
                            <br />
                            Информацию об ошибках просьба отправлять на email:
                            <a href="mailto:info@prometal.ru">info@prometal.ru</a>
                        </div>
                    </div>
                </div>
                {true && (
                    <div className={styles.drankBlock}>
                        <span className={styles.error}>
                            В проекте присутствуют габаритные размеры вертикалей, требующие отпила. Вам будет необходимо
                            отпилить их самостоятельно.
                        </span>
                        {/* <FormGroup>
                        <FormControlLabel control={<Checkbox
                            defaultChecked
                            sx={{
                                color: '#EB3D00',
                                '&.Mui-checked': {
                                    color: '#EB3D00',
                                },
                            }}
                        />} label={<Typography style={{
                            fontSize: 14,
                            fontWeight: 700,
                            lineHeight: '17px',
                        }}>Заказать отпил направляющих</Typography>}/>
                    </FormGroup> */}
                    </div>
                )}
                <div className={styles.toggleButtons}>
                    <ToggleButtons
                        array={arrayGeneralListSpecification}
                        selectIndex={generalListSpecification ? 0 : 1}
                        setSelect={setGeneralListSpecification}
                    />
                </div>
                <div
                    className={[styles.nomenklature, windowDebug && styles.nomenklatureDebug].filter(Boolean).join(" ")}
                >
                    {generalListSpecification ? <SpecificationGeneral /> : <SpecificationBySections />}
                </div>
            </div>
        </Stack>
    );
});
